import React, { useState, useEffect } from 'react';
import { X, Upload, Plus, Trash2, Edit3, ShieldCheck, AlertCircle, RefreshCw } from 'lucide-react';
import { CollectionItem, CaptchaData, DictionaryType } from '../../types';
import { collectionService, RawFileToUpload } from '../../services/collectionService';
import { secretKeyService } from '../../services/secretKeyService';
import { useToast } from '../../context/ToastContext';

interface CollectionEditModalProps {
  collection: CollectionItem;
  onClose: () => void;
  onSuccess: (updated: CollectionItem) => void;
}

export const CollectionEditModal: React.FC<CollectionEditModalProps> = ({
  collection,
  onClose,
  onSuccess,
}) => {
  const toast = useToast();

  const [name, setName] = useState(collection.name);
  const [author, setAuthor] = useState(collection.author);
  const [files, setFiles] = useState<RawFileToUpload[]>([]);
  const [loadingFiles, setLoadingFiles] = useState(true);

  const [captcha, setCaptcha] = useState<CaptchaData | null>(null);
  const [captchaAnswer, setCaptchaAnswer] = useState('');
  const [loadingCaptcha, setLoadingCaptcha] = useState(false);

  const [updating, setUpdating] = useState(false);
  const [progressMsg, setProgressMsg] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadExistingFiles();
    fetchCaptcha();
  }, [collection]);

  const loadExistingFiles = async () => {
    setLoadingFiles(true);
    try {
      const loaded = await collectionService.fetchZipFileContents(collection.zipUrlCurrent);
      setFiles(loaded);
    } catch (e: any) {
      console.error(e);
      toast.error('Không thể nạp nội dung file zip cũ. Vui lòng tải lại trang.');
    } finally {
      setLoadingFiles(false);
    }
  };

  const fetchCaptcha = async () => {
    setLoadingCaptcha(true);
    try {
      const data = await collectionService.generateCaptcha();
      setCaptcha(data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingCaptcha(false);
    }
  };

  const inferType = (filename: string): DictionaryType => {
    const lower = filename.toLowerCase();
    if (lower.includes('vietphrase')) return 'VietPhrase';
    if (lower.includes('name')) return 'Name';
    if (lower.includes('phienam')) return 'PhienAm';
    if (lower.includes('pronoun')) return 'Pronouns';
    if (lower.includes('rule')) return 'Rule';
    return 'VietPhrase';
  };

  const handleAddFiles = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files;
    if (!selected || selected.length === 0) return;

    if (files.length + selected.length > 15) {
      toast.error('Tối đa được lưu 15 file trong một bộ sưu tập.');
      return;
    }

    const newFiles: RawFileToUpload[] = [];
    const promises: Promise<void>[] = [];

    Array.from(selected).forEach((file) => {
      if (!file.name.endsWith('.txt')) {
        toast.error(`File ${file.name} không đúng định dạng .txt`);
        return;
      }
      if (file.size > 50 * 1024 * 1024) {
        toast.error(`File ${file.name} quá lớn (tối đa 50MB/file)`);
        return;
      }

      const p = new Promise<void>((resolve) => {
        const reader = new FileReader();
        reader.onload = (evt) => {
          const content = evt.target?.result as string;
          // Check if file with same name already exists -> Replace or Warn
          const existingIdx = files.findIndex((f) => f.name.toLowerCase() === file.name.toLowerCase());
          if (existingIdx >= 0) {
            toast.info(`Đã thay thế nội dung file ${file.name}`);
          }
          newFiles.push({
            name: file.name,
            type: inferType(file.name),
            content,
          });
          resolve();
        };
        reader.readAsText(file, 'UTF-8');
      });
      promises.push(p);
    });

    Promise.all(promises).then(() => {
      setFiles((prev) => {
        const uniqueNew = newFiles.filter(
          (nf, idx, self) => idx === self.findIndex((f) => f.name.toLowerCase() === nf.name.toLowerCase())
        );
        const filteredPrev = prev.filter(
          (p) => !uniqueNew.some((n) => n.name.toLowerCase() === p.name.toLowerCase())
        );
        return [...filteredPrev, ...uniqueNew];
      });
    });
  };

  const handleRemoveFile = (index: number) => {
    if (files.length <= 1) {
      toast.warning('Bộ Sưu Tập phải chứa ít nhất 1 file .txt.');
      return;
    }
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!name.trim()) {
      setError('Vui lòng nhập Tên Bộ Sưu Tập.');
      return;
    }
    if (files.length === 0) {
      setError('Bộ Sưu Tập phải chứa ít nhất 1 file .txt.');
      return;
    }
    if (!captchaAnswer.trim()) {
      setError('Vui lòng nhập câu trả lời CAPTCHA.');
      return;
    }

    setUpdating(true);

    try {
      // 1. Get user recovery code & hash
      const recoveryCode = secretKeyService.getUserRecoveryCode();
      const secretKeyHash = await secretKeyService.hashKey(recoveryCode);

      // 2. Perform update
      const updatedItem = await collectionService.updateCollection({
        collectionId: collection.id,
        name: name.trim(),
        author: author.trim() || undefined,
        secretKeyHash,
        captcha: {
          nonce: captcha?.nonce || '',
          questionId: captcha?.questionId || '',
          answer: captchaAnswer.trim(),
        },
        rawFiles: files,
        onProgress: (msg) => setProgressMsg(msg),
      });

      toast.success('Đã cập nhật Bộ Sưu Tập thành công! Bản cũ đã được sao lưu an toàn.');
      onSuccess(updatedItem);
    } catch (err: any) {
      console.error(err);
      setError(err.message || 'Lỗi khi cập nhật Bộ Sưu Tập.');
      toast.error(err.message || 'Lỗi khi cập nhật Bộ Sưu Tập.');
      fetchCaptcha();
      setCaptchaAnswer('');
    } finally {
      setUpdating(false);
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(15, 23, 42, 0.75)',
        backdropFilter: 'blur(6px)',
        WebkitBackdropFilter: 'blur(6px)',
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '1rem',
        boxSizing: 'border-box',
      }}
    >
      <div
        style={{
          backgroundColor: '#ffffff',
          borderRadius: '1rem',
          width: '100%',
          maxWidth: '560px',
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: '1rem 1.25rem',
            borderBottom: '1px solid var(--border-light)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            backgroundColor: '#fafafa',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <div style={{ padding: '0.375rem', borderRadius: '0.375rem', backgroundColor: '#e0f2fe', color: '#0284c7', display: 'flex' }}>
              <Edit3 size={18} />
            </div>
            <h3 style={{ fontSize: '1.0625rem', fontWeight: 700, margin: 0, color: 'var(--text-main)' }}>
              Cập Nhật / Sửa Bộ Sưu Tập
            </h3>
          </div>
          <button
            onClick={onClose}
            disabled={updating}
            style={{
              padding: '0.375rem',
              borderRadius: '0.375rem',
              backgroundColor: '#f1f5f9',
              color: '#64748b',
              border: 'none',
              cursor: updating ? 'not-allowed' : 'pointer',
            }}
          >
            <X size={18} />
          </button>
        </div>

        {/* Scrollable Body */}
        <div style={{ padding: '1.25rem', overflowY: 'auto', flex: 1 }}>
          {error && (
            <div
              style={{
                backgroundColor: '#fef2f2',
                border: '1px solid #fecaca',
                borderRadius: '0.5rem',
                padding: '0.75rem 1rem',
                marginBottom: '1rem',
                color: '#991b1b',
                fontSize: '0.8125rem',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
              }}
            >
              <AlertCircle size={16} style={{ flexShrink: 0 }} />
              <div>{error}</div>
            </div>
          )}

          {loadingFiles ? (
            <div style={{ padding: '2.5rem 1rem', textAlign: 'center', color: '#64748b' }}>
              <RefreshCw size={28} className="animate-spin" style={{ margin: '0 auto 0.75rem auto', color: 'var(--action-primary)' }} />
              <p style={{ fontSize: '0.875rem', fontWeight: 600 }}>Đang nạp danh sách file từ server...</p>
            </div>
          ) : (
            <form id="edit-collection-form" onSubmit={handleSubmit}>
              {/* Collection Name Input */}
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', fontSize: '0.8125rem', fontWeight: 700, color: 'var(--text-main)', marginBottom: '0.375rem' }}>
                  Tên Bộ Sưu Tập: *
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  maxLength={100}
                  required
                  placeholder="VD: Bộ VP Đọc Tiên Hiệp Mới Nhất..."
                  style={{
                    width: '100%',
                    padding: '0.625rem 0.75rem',
                    borderRadius: '0.375rem',
                    border: '1px solid var(--border-light)',
                    fontSize: '0.875rem',
                  }}
                />
              </div>

              {/* Author Name Input */}
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', fontSize: '0.8125rem', fontWeight: 700, color: 'var(--text-main)', marginBottom: '0.375rem' }}>
                  Tên Tác Giả / Nguồn:
                </label>
                <input
                  type="text"
                  value={author}
                  onChange={(e) => setAuthor(e.target.value)}
                  maxLength={50}
                  placeholder="VD: Nguyễn Văn A..."
                  style={{
                    width: '100%',
                    padding: '0.625rem 0.75rem',
                    borderRadius: '0.375rem',
                    border: '1px solid var(--border-light)',
                    fontSize: '0.875rem',
                  }}
                />
              </div>

              {/* File List Header & Add File */}
              <div style={{ marginBottom: '1.25rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                  <label style={{ fontSize: '0.8125rem', fontWeight: 700, color: 'var(--text-main)' }}>
                    Danh Sách File Trong Gói ({files.length}/15 file):
                  </label>
                  <label
                    htmlFor="add-file-input"
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '0.25rem',
                      fontSize: '0.8125rem',
                      fontWeight: 700,
                      color: '#0284c7',
                      cursor: 'pointer',
                      backgroundColor: '#e0f2fe',
                      padding: '0.375rem 0.625rem',
                      borderRadius: '0.375rem',
                    }}
                  >
                    <Plus size={14} />
                    Thêm / Đè file .txt
                  </label>
                  <input
                    id="add-file-input"
                    type="file"
                    multiple
                    accept=".txt"
                    onChange={handleAddFiles}
                    style={{ display: 'none' }}
                  />
                </div>

                {/* File Cards List */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', maxHeight: '180px', overflowY: 'auto' }}>
                  {files.map((file, idx) => (
                    <div
                      key={`${file.name}-${idx}`}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '0.5rem 0.75rem',
                        backgroundColor: '#f8fafc',
                        border: '1px solid var(--border-light)',
                        borderRadius: '0.375rem',
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', minWidth: 0 }}>
                        <span
                          style={{
                            fontSize: '0.6875rem',
                            fontWeight: 700,
                            padding: '0.125rem 0.375rem',
                            borderRadius: '0.25rem',
                            backgroundColor: 'var(--accent-highlight)',
                            color: 'var(--text-on-light)',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {file.type}
                        </span>
                        <span style={{ fontSize: '0.8125rem', fontWeight: 600, color: 'var(--text-main)', textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}>
                          {file.name}
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleRemoveFile(idx)}
                        title="Xóa file này khỏi gói"
                        style={{
                          padding: '0.25rem',
                          color: 'var(--accent-danger)',
                          borderRadius: '0.25rem',
                          border: 'none',
                          cursor: 'pointer',
                        }}
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              {/* CAPTCHA Section */}
              <div
                style={{
                  backgroundColor: '#f0f9ff',
                  border: '1px solid #bae6fd',
                  borderRadius: '0.5rem',
                  padding: '0.875rem 1rem',
                  marginBottom: '1rem',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem', marginBottom: '0.375rem', color: '#0369a1', fontWeight: 700, fontSize: '0.8125rem' }}>
                  <ShieldCheck size={16} />
                  Mã xác nhận (CAPTCHA)
                </div>
                {loadingCaptcha ? (
                  <p style={{ fontSize: '0.75rem', color: '#64748b', margin: 0 }}>Đang tải CAPTCHA...</p>
                ) : captcha ? (
                  <div>
                    <p style={{ fontSize: '0.8125rem', fontWeight: 600, color: '#0f172a', marginBottom: '0.375rem' }}>
                      {captcha.questionText}
                    </p>
                    <input
                      type="text"
                      placeholder="Nhập câu trả lời..."
                      value={captchaAnswer}
                      onChange={(e) => setCaptchaAnswer(e.target.value)}
                      required
                      style={{
                        width: '100%',
                        padding: '0.4rem 0.625rem',
                        borderRadius: '0.375rem',
                        border: '1px solid #93c5fd',
                        fontSize: '0.8125rem',
                        backgroundColor: '#ffffff',
                      }}
                    />
                  </div>
                ) : (
                  <p style={{ fontSize: '0.75rem', color: 'red' }}>Không thể tải CAPTCHA. Vui lòng thử lại.</p>
                )}
              </div>
            </form>
          )}
        </div>

        {/* Footer Actions */}
        <div
          style={{
            padding: '1rem 1.25rem',
            borderTop: '1px solid var(--border-light)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: '0.5rem',
            backgroundColor: '#fafafa',
          }}
        >
          <button
            type="button"
            onClick={onClose}
            disabled={updating}
            style={{
              padding: '0.5rem 1rem',
              borderRadius: '0.375rem',
              backgroundColor: '#f1f5f9',
              color: '#475569',
              fontWeight: 600,
              fontSize: '0.875rem',
              border: 'none',
              cursor: updating ? 'not-allowed' : 'pointer',
            }}
          >
            Hủy
          </button>
          <button
            type="submit"
            form="edit-collection-form"
            disabled={updating || loadingFiles}
            style={{
              padding: '0.5rem 1.25rem',
              borderRadius: '0.375rem',
              backgroundColor: 'var(--primary-action)',
              color: '#0f172a',
              fontWeight: 700,
              fontSize: '0.875rem',
              border: 'none',
              cursor: updating || loadingFiles ? 'wait' : 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.375rem',
            }}
          >
            {updating ? (
              <>
                <RefreshCw size={16} className="animate-spin" />
                <span>{progressMsg || 'Đang cập nhật...'}</span>
              </>
            ) : (
              <>
                <Upload size={16} />
                <span>Lưu thay đổi</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};
